import { ScrollToTop } from '@lmt/unicorn-shared/app/components/common/table/helper';
import { triggerDownloadFileDialog } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import React, { useCallback, useMemo, useState } from 'react';
import {
  DXRG,
  Grid,
  Sorting,
  SortingDirection,
  Table,
  TableBase,
  TableColumnWidthInfo,
  TableHeaderRow,
} from '../common/devexpress/ReactGrid/dx-react-grid';
import exceljs from 'exceljs';

import styles from './devextreme-helper.module.scss';
import { useEventCallback } from '@mui/material';

export function useScrollingState(pageSize = 100) {
  const [scrollParams, setScrollParams] = useState({
    skip: 0,
    take: pageSize * 2,
  });

  const getRows = useCallback((skip: number, take: number) => {
    setScrollParams({ skip, take });
  }, []);

  const scrollToStart = useEventCallback(() => {
    ScrollTableOnScreenToTop();
    setScrollParams({ skip: 0, take: pageSize * 2 });
  });

  return { scrollParams, getRows, scrollToStart, pageSize };
}

export function useColumnResizingState(
  defaultColumnWidths: TableColumnWidthInfo[],
  disabledColumns: string[],
) {
  const [columnWidths, setColumnWidths] = useState(defaultColumnWidths);

  const onColumnWidthsChange = useEventCallback(
    (newColumnWidths: TableColumnWidthInfo[]) => {
      const filtered = newColumnWidths.filter(
        (x) => !disabledColumns.includes(x.columnName),
      );
      const allColumns = filtered.concat(
        columnWidths.filter((x) => disabledColumns.includes(x.columnName)),
      );
      setColumnWidths(allColumns);
    },
  );

  return { columnWidths, onColumnWidthsChange };
}

export function ScrollTableOnScreenToTop() {
  const tableContainerRoots = document.querySelectorAll<HTMLDivElement>(
    "[class*='table-container-root']",
  );
  for (const tableContainerRoot of tableContainerRoots) {
    //offsetParent is fine in our case because it is null for hidden tabs (because of 'display: none')
    if (
      tableContainerRoot.offsetParent !== null &&
      tableContainerRoot.firstElementChild
    ) {
      ScrollToTop(tableContainerRoot.firstElementChild);
    }
  }
}

export function useConvertedSorting(
  queryParams: { sortBy?: string | null; desc?: boolean },
  setQueryParams: (sorting: { sortBy?: string | null; desc?: boolean }) => void,
  scrollToStart: () => void,
) {
  const sorting = useMemo(() => {
    return queryParams.sortBy
      ? [
          {
            columnName: queryParams.sortBy,
            direction: queryParams.desc
              ? ('desc' as SortingDirection)
              : ('asc' as SortingDirection),
          },
        ]
      : [];
  }, [queryParams.sortBy, queryParams.desc]);

  const setSorting = useEventCallback((param: Sorting[]) => {
    scrollToStart();
    setQueryParams({
      sortBy: param[0].columnName,
      desc: param[0].direction == 'desc',
    });
  });

  return { sorting, setSorting };
}

//when column.title is ''|null|undefined, column.name is shown in the header row, so we use this custom content cell
export const HideNullTitlesHeaderContent = (
  props: TableHeaderRow.ContentProps,
) => (
  <DXRG.TableHeaderRow.Content {...props}>
    {props.column.title ? props.children : undefined}
  </DXRG.TableHeaderRow.Content>
);

export const FullHeightGridRoot = (props: Grid.RootProps) => (
  <DXRG.Grid.Root
    {...props}
    style={{ height: '100%' }}
    className={'table-container-root'} //className is used to find table root for scrolling to top
  />
);

export const GetStyledRow =
  (onClick: (row: any) => void) => (props: Table.DataRowProps) => (
    <DXRG.Table.Row {...props} onClick={() => onClick(props.row)} />
  );

export const HoverableTable = (props: object) => (
  <DXRG.Table.Table {...props} className={styles.table} />
);

export const StickyHeaderRow = (props: TableBase.RowProps) => (
  <DXRG.TableHeaderRow.Row {...props} className={styles.stickyRow} />
);

export function useDataGridExportHandler(props: { fileName: string }) {
  const callback = useCallback(
    (event: ExportingEvent) => {
      const workbook = new exceljs.Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');

      exportDataGrid({
        component: event.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          triggerDownloadFileDialog(
            new Blob([buffer], { type: 'application/octet-stream' }),
            `${props.fileName}.xlsx`,
          );
        });
      });
    },
    [props.fileName],
  );

  return callback;
}
